import { type GroupChannel } from "@sendbird/chat/groupChannel";
import { type UserMessageCreateParams } from "@sendbird/chat/message";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface SendTextMessageParams {
  channel: GroupChannel;
  message: string;
  parentMessageId?: number;
}

/**
 * Mutation hook to send a text message to a Sendbird channel.
 * Automatically attaches workplace user metadata to the message.
 *
 * @example
 * ```tsx
 * const { mutate: sendMessage, isPending } = useSendTextMessage();
 *
 * sendMessage({
 *   channel,
 *   message: "Hello!",
 *   parentMessageId: 123, // Optional, for replies
 * });
 * ```
 */
export function useSendTextMessage() {
  const queryClient = useQueryClient();
  const session = useSession();

  return useMutation({
    mutationFn: async ({ channel, message, parentMessageId }: SendTextMessageParams) => {
      const trimmedMessage = message.trim();
      if (!trimmedMessage) {
        throw new Error("Message cannot be empty");
      }

      const params: UserMessageCreateParams = {
        message: trimmedMessage,
        data: JSON.stringify({
          workplaceUserId: session.user?._id,
          workplaceUserName: session.user?.name,
        }),
      };

      // Add parent message if replying
      if (parentMessageId) {
        params.parentMessageId = parentMessageId;
        // Required for reply to appear in main channel feed
        params.isReplyToChannel = true;
      }

      // Wrap MessageRequestHandler in a promise
      return await new Promise((resolve, reject) => {
        const handler = channel.sendUserMessage(params);
        handler
          .onSucceeded((message) => {
            resolve(message);
          })
          .onFailed((error) => {
            reject(error);
          });
      });
    },
    onSuccess: (_, variables) => {
      // Force immediate refetch of messages
      void queryClient.refetchQueries({
        queryKey: ["chat", "messages", variables.channel.url],
      });
    },
    meta: {
      userErrorMessage: "Failed to send message",
    },
  });
}
